import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ShieldCheck, 
  Search, 
  MessageSquareWarning, 
  Globe, 
  LifeBuoy, 
  FileCheck2, 
  Users, 
  ArrowRight, 
  CheckCircle2, 
  AlertTriangle, 
  Lock, 
  Eye, 
  BellRing, 
  Sparkles, 
  ChevronRight,
  Fingerprint,
  FileSpreadsheet,
  Building2,
  HelpCircle,
  Clock,
  BookOpen,
  Zap,
  PhoneCall,
  Laptop
} from 'lucide-react';
import { ScoreGauge } from '../components/common/ScoreGauge';
import { useStore } from '../store/useStore';
import { RESOURCE_ARTICLES } from '../data/resources';

export const Home: React.FC = () => {
  const navigate = useNavigate();
  const { openAiModal, securityScore } = useStore();
  const [quickSearchInput, setQuickSearchInput] = useState('');

  const handleQuickCheck = (e: React.FormEvent) => {
    e.preventDefault();
    if (quickSearchInput.trim()) {
      navigate(`/check-identity?q=${encodeURIComponent(quickSearchInput.trim())}`);
    } else {
      navigate('/check-identity');
    }
  };

  const featuredGuides = RESOURCE_ARTICLES.slice(0, 3);

  return (
    <div className="space-y-24 pb-20">
      {/* HERO SECTION */}
      <section className="relative pt-12 pb-16 overflow-hidden">
        {/* Ambient background glow */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[450px] bg-emerald-500/10 blur-[130px] rounded-full pointer-events-none -z-10" />
        <div className="absolute top-1/3 right-10 w-[400px] h-[350px] bg-blue-500/10 blur-[120px] rounded-full pointer-events-none -z-10" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Col: Headline & Pitch */}
            <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
              {/* Australian Trust Badge */}
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-950/80 border border-emerald-800/80 text-xs font-semibold text-emerald-300">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span>AUSTRALIA'S PERSONAL IDENTITY SECURITY CENTRE</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.15]">
                Is your identity <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-teal-300 to-cyan-400">at risk?</span>
              </h1>

              <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto lg:mx-0 font-normal leading-relaxed">
                Check your exposure, detect suspicious scams, and get clear, step-by-step guidance to protect your Australian documents and financial accounts.
              </p>

              {/* Core Philosophy Pill */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 text-xs font-medium text-slate-400 pt-1">
                <span className="px-2.5 py-1 rounded bg-slate-900 border border-slate-800 text-slate-300">1. Detect Exposure</span>
                <span>→</span>
                <span className="px-2.5 py-1 rounded bg-slate-900 border border-slate-800 text-slate-300">2. Understand Risk</span>
                <span>→</span>
                <span className="px-2.5 py-1 rounded bg-slate-900 border border-slate-800 text-slate-300">3. Protect Documents</span>
                <span>→</span>
                <span className="px-2.5 py-1 rounded bg-slate-900 border border-slate-800 text-slate-300">4. Recover Step-by-Step</span>
              </div>

              {/* Fast Quick Search Form */}
              <form onSubmit={handleQuickCheck} className="pt-2 max-w-xl mx-auto lg:mx-0">
                <div className="flex flex-col sm:flex-row gap-2 bg-slate-900/90 p-2 rounded-2xl border border-slate-700/80 shadow-2xl">
                  <div className="relative flex-1">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      value={quickSearchInput}
                      onChange={(e) => setQuickSearchInput(e.target.value)}
                      placeholder="Enter email, phone, or username to check..."
                      className="w-full pl-11 pr-4 py-3 bg-transparent text-white placeholder-slate-500 text-sm focus:outline-none"
                    />
                  </div>
                  <button
                    type="submit"
                    className="px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-semibold text-sm shadow-lg shadow-emerald-950/40 transition-all flex items-center justify-center gap-2 flex-shrink-0"
                  >
                    <span>Check My Identity</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs text-slate-400 mt-2 flex items-center justify-center lg:justify-start gap-1.5">
                  <Lock className="w-3.5 h-3.5 text-emerald-400" />
                  <span>We never ask for passwords. 100% confidential and privacy-first.</span>
                </p>
              </form>

              {/* Secondary CTAs */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 pt-2">
                <Link
                  to="/scam-checker"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-200 text-sm font-medium transition-colors"
                >
                  <MessageSquareWarning className="w-4 h-4 text-amber-400" />
                  <span>Check a Suspicious Scam</span>
                </Link>
                <Link
                  to="/website-checker"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-200 text-sm font-medium transition-colors"
                >
                  <Globe className="w-4 h-4 text-blue-400" />
                  <span>Analyze a Website URL</span>
                </Link>
              </div>
            </div>

            {/* Right Col: Interactive Visual Identity Security Dashboard Preview with Imagery */}
            <div className="lg:col-span-5 space-y-4">
              <div className="relative">
                {/* Decorative border frame */}
                <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500/30 to-teal-500/20 rounded-3xl blur-xl -z-10" />

                <div className="bg-slate-900/95 border border-slate-800 rounded-3xl shadow-2xl p-6 space-y-6 overflow-hidden">
                  {/* Top Bar Preview */}
                  <div className="flex items-center justify-between border-b border-slate-800 pb-4">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-emerald-600/20 border border-emerald-500/30 flex items-center justify-center">
                        <Fingerprint className="w-4 h-4 text-emerald-400" />
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-white">Identity Health Overview</h4>
                        <p className="text-[11px] text-slate-400">Live Continuous Telemetry</p>
                      </div>
                    </div>
                    <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-950 text-emerald-300 border border-emerald-800">
                      ACTIVE MONITORING
                    </span>
                  </div>

                  {/* Score & Gauge Preview */}
                  <div className="flex items-center gap-6 bg-slate-950/80 p-4 rounded-2xl border border-slate-800">
                    <ScoreGauge score={securityScore.overallScore} size="sm" showIcon={false} />
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-emerald-400" />
                        <span className="text-xs font-semibold text-slate-200">Security Score: 82 / 100</span>
                      </div>
                      <p className="text-xs text-slate-400 leading-snug">
                        Your identity security is strong. 2 documents monitored, no active credit queries.
                      </p>
                      <Link to="/security-score" className="text-xs font-semibold text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1 pt-1">
                        <span>Improve My Score</span>
                        <ChevronRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>

                  {/* Live Mini Cards */}
                  <div className="space-y-2.5 text-xs">
                    <div className="flex items-center justify-between p-3 rounded-xl bg-amber-950/30 border border-amber-800/40 text-amber-200">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                        <span className="font-medium">1 Past Breach Exposure Detected</span>
                      </div>
                      <Link to="/alerts" className="text-[11px] font-bold text-amber-300 hover:underline">
                        Review Alert
                      </Link>
                    </div>

                    <div className="flex items-center justify-between p-3 rounded-xl bg-slate-950 border border-slate-800 text-slate-300">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                        <span>NSW Driver Licence Card # Monitored</span>
                      </div>
                      <span className="text-[11px] text-emerald-400 font-mono">DVS Validated</span>
                    </div>

                    <div className="flex items-center justify-between p-3 rounded-xl bg-slate-950 border border-slate-800 text-slate-300">
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-blue-400 flex-shrink-0" />
                        <span>Credit Inquiries Shield Active (Equifax)</span>
                      </div>
                      <span className="text-[11px] text-slate-400">Zero Inquiries</span>
                    </div>
                  </div>

                  {/* CTA link to full dashboard */}
                  <Link
                    to="/dashboard"
                    className="w-full py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-medium text-xs flex items-center justify-center gap-1.5 transition-colors"
                  >
                    <span>Open Live Personal Dashboard</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST & AUSTRALIAN REALITY SECTION */}
      <section className="border-y border-slate-800/80 bg-slate-900/40 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="space-y-1.5">
              <div className="text-2xl sm:text-3xl font-extrabold text-white">Australian-Focused</div>
              <p className="text-xs text-slate-400">Designed around DVS, myGov, Medicare, ATO & state licences</p>
            </div>

            <div className="space-y-1.5">
              <div className="text-2xl sm:text-3xl font-extrabold text-emerald-400">Privacy-First</div>
              <p className="text-xs text-slate-400">Data minimisation. We never ask for or store your passwords</p>
            </div>

            <div className="space-y-1.5">
              <div className="text-2xl sm:text-3xl font-extrabold text-white">Actionable Steps</div>
              <p className="text-xs text-slate-400">Don't just get alerted. Get exact step-by-step remediation plans</p>
            </div>

            <div className="space-y-1.5">
              <div className="text-2xl sm:text-3xl font-extrabold text-teal-400">Official Standards</div>
              <p className="text-xs text-slate-400">Aligned with IDCARE, ACSC Cyber.gov.au & Scamwatch guidance</p>
            </div>
          </div>
        </div>
      </section>

      {/* 4-STAGE CORE PHILOSOPHY BREAKDOWN (WITH IMAGERY) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-900 text-slate-300 text-xs font-semibold border border-slate-800">
            <Zap className="w-3.5 h-3.5 text-emerald-400" />
            <span>FOUR-STAGE IDENTITY DEFENCE</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
            Know what's happening with your identity. <span className="text-emerald-400">Protect it before fraud happens.</span>
          </h2>
          <p className="text-base text-slate-300">
            We avoid fear-mongering and panic. Instead, we give every Australian clear, methodical control across four essential stages.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* 1. Detect */}
          <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-xl hover:border-emerald-700/60 transition-all flex flex-col justify-between group">
            <div>
              <div className="h-40 overflow-hidden relative">
                <img
                  src="https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=600&auto=format&fit=crop&q=80"
                  alt="Detect Data Breaches"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3 w-8 h-8 rounded-xl bg-slate-950/80 backdrop-blur-md border border-emerald-800 flex items-center justify-center text-emerald-400 font-extrabold text-xs">
                  01
                </div>
              </div>
              <div className="p-6 space-y-2">
                <h3 className="text-base font-bold text-white group-hover:text-emerald-400 transition-colors">1. Detect Exposure</h3>
                <p className="text-xs text-slate-300 leading-relaxed">
                  Surveil whether your email, mobile number, name, or credentials have appeared in Australian or international corporate data breaches.
                </p>
              </div>
            </div>
            <div className="px-6 pb-6 pt-2 border-t border-slate-800/80">
              <Link to="/check-identity" className="text-xs font-bold text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1">
                <span>Check Exposure Now</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

          {/* 2. Understand */}
          <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-xl hover:border-blue-700/60 transition-all flex flex-col justify-between group">
            <div>
              <div className="h-40 overflow-hidden relative">
                <img
                  src="https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=600&auto=format&fit=crop&q=80"
                  alt="Understand Scam Risk"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3 w-8 h-8 rounded-xl bg-slate-950/80 backdrop-blur-md border border-blue-800 flex items-center justify-center text-blue-400 font-extrabold text-xs">
                  02
                </div>
              </div>
              <div className="p-6 space-y-2">
                <h3 className="text-base font-bold text-white group-hover:text-blue-400 transition-colors">2. Understand Risk</h3>
                <p className="text-xs text-slate-300 leading-relaxed">
                  Analyze suspicious SMS messages, fake Linkt toll demands, myGov tax refund emails, and risky URLs with our AI Scam Engine.
                </p>
              </div>
            </div>
            <div className="px-6 pb-6 pt-2 border-t border-slate-800/80">
              <Link to="/scam-checker" className="text-xs font-bold text-blue-400 hover:text-blue-300 inline-flex items-center gap-1">
                <span>Analyze Scam Message</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

          {/* 3. Protect */}
          <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-xl hover:border-teal-700/60 transition-all flex flex-col justify-between group">
            <div>
              <div className="h-40 overflow-hidden relative">
                <img
                  src="https://images.unsplash.com/photo-1557597774-9d273605dfa9?w=600&auto=format&fit=crop&q=80"
                  alt="Protect Australian Documents"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3 w-8 h-8 rounded-xl bg-slate-950/80 backdrop-blur-md border border-teal-800 flex items-center justify-center text-teal-400 font-extrabold text-xs">
                  03
                </div>
              </div>
              <div className="p-6 space-y-2">
                <h3 className="text-base font-bold text-white group-hover:text-teal-400 transition-colors">3. Protect Documents</h3>
                <p className="text-xs text-slate-300 leading-relaxed">
                  Safeguard Driver Licence Card Numbers, Medicare, and TFNs. Apply digital watermarks before sharing ID copies for rent or hire.
                </p>
              </div>
            </div>
            <div className="px-6 pb-6 pt-2 border-t border-slate-800/80">
              <Link to="/id-watermark" className="text-xs font-bold text-teal-400 hover:text-teal-300 inline-flex items-center gap-1">
                <span>Generate ID Watermark</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

          {/* 4. Recover */}
          <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-xl hover:border-rose-700/60 transition-all flex flex-col justify-between group">
            <div>
              <div className="h-40 overflow-hidden relative">
                <img
                  src="https://images.unsplash.com/photo-1450133064473-71024230f91b?w=600&auto=format&fit=crop&q=80"
                  alt="Recover from Identity Theft"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3 w-8 h-8 rounded-xl bg-slate-950/80 backdrop-blur-md border border-rose-800 flex items-center justify-center text-rose-400 font-extrabold text-xs">
                  04
                </div>
              </div>
              <div className="p-6 space-y-2">
                <h3 className="text-base font-bold text-white group-hover:text-rose-400 transition-colors">4. Recover Step-by-Step</h3>
                <p className="text-xs text-slate-300 leading-relaxed">
                  Follow an interactive 8-step recovery roadmap connecting you with IDCARE, Equifax credit bans, and state road registries.
                </p>
              </div>
            </div>
            <div className="px-6 pb-6 pt-2 border-t border-slate-800/80">
              <Link to="/identity-theft" className="text-xs font-bold text-rose-400 hover:text-rose-300 inline-flex items-center gap-1">
                <span>Start Emergency Response</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* AUSTRALIAN DOCUMENT HARDENING & WATERMARKING SPOTLIGHT SECTION */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 sm:p-12 shadow-2xl overflow-hidden relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-950 text-teal-300 text-xs font-bold border border-teal-800">
                <FileCheck2 className="w-3.5 h-3.5" />
                <span>AUSTRALIAN DVS & DOCUMENT HARDENING</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                Protecting Driver Licences, Passports & Medicare Cards
              </h2>
              <p className="text-slate-300 text-sm leading-relaxed">
                When you share photos of your driver licence or passport for rental applications, hotel bookings, or job verifications, raw scans often leak in commercial breaches.
              </p>
              <p className="text-slate-300 text-sm leading-relaxed">
                Our in-browser <strong>ID Watermark Tool</strong> embeds un-removable purpose stamps (e.g. <em>"COPY — FOR TENANCY AT RAY WHITE ONLY — 20 AUG 2026"</em>) directly on your device, preventing criminal reuse across the Australian Document Verification Service (DVS).
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <Link
                  to="/id-watermark"
                  className="px-6 py-3 rounded-xl bg-teal-600 hover:bg-teal-500 text-white font-bold text-xs sm:text-sm shadow-lg shadow-teal-950/40 transition-colors flex items-center gap-2"
                >
                  <span>Open Free ID Watermark Tool</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/identity-documents"
                  className="px-6 py-3 rounded-xl bg-slate-950 hover:bg-slate-800 border border-slate-700 text-slate-200 font-semibold text-xs sm:text-sm transition-colors"
                >
                  View State Licence Guides
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden border border-slate-800 shadow-2xl group">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&auto=format&fit=crop&q=80"
                  alt="Australian Document Verification"
                  className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent flex flex-col justify-end p-6">
                  <span className="text-xs font-bold text-emerald-400 uppercase font-mono">100% Client-Side Privacy</span>
                  <h4 className="text-base font-bold text-white">Images never leave your browser</h4>
                  <p className="text-xs text-slate-300">Stamps custom text, dates, and purpose watermarks in seconds.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LIVE INTERACTIVE AI SCAM CHECKER SHOWCASE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-br from-slate-900 via-slate-900 to-navy-950 border border-slate-800 rounded-3xl p-8 sm:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-950 text-amber-300 text-xs font-semibold border border-amber-800">
                <MessageSquareWarning className="w-3.5 h-3.5" />
                <span>AI Scam & Phishing Detection</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
                Received a strange text or email? <span className="text-amber-400">Check it in seconds.</span>
              </h2>
              <p className="text-slate-300 text-sm leading-relaxed">
                Scammers target millions of Australians daily with fake toll notices (Linkt), Australia Post parcel updates, myGov tax refunds, and PayID marketplace tricks. Paste any message into our analyzer to receive an instant risk rating and safe response advice.
              </p>
              <div className="flex flex-wrap gap-4 pt-2">
                <Link
                  to="/scam-checker"
                  className="px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-sm transition-colors flex items-center gap-2"
                >
                  <MessageSquareWarning className="w-4 h-4" />
                  <span>Open Scam Checker</span>
                </Link>
                <button
                  onClick={openAiModal}
                  className="px-5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-medium text-sm transition-colors flex items-center gap-2"
                >
                  <Sparkles className="w-4 h-4 text-emerald-400" />
                  <span>Ask AI Assistant</span>
                </button>
              </div>
            </div>

            {/* Visual Example Card with Real-time Analysis Breakdown */}
            <div className="bg-slate-950/90 border border-slate-800 p-6 rounded-2xl shadow-xl space-y-4 font-mono text-xs">
              <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                <span className="text-slate-400 font-sans font-bold">SAMPLE THREAT ANALYSIS RESULT</span>
                <span className="px-2.5 py-0.5 rounded bg-rose-950 text-rose-300 border border-rose-800 font-bold">
                  HIGH RISK (96%)
                </span>
              </div>
              <div className="bg-slate-900 p-3 rounded-xl text-slate-300 border border-slate-800 italic">
                "AusPost: Your package AU-88219 is on hold due to $2.35 unpaid fee. Update within 24hrs at https://auspost-redelivery.net"
              </div>
              <div className="space-y-1.5 text-slate-400 font-sans text-xs">
                <p className="text-rose-400 font-bold">Why is this a scam?</p>
                <p>• Impersonation of Australia Post using fraudulent non-official domain (.net).</p>
                <p>• Artificial urgency (24 hours) designed to provoke hasty card payment.</p>
                <p>• Solicits credit card details for a tiny handling fee.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED ARTICLES & KNOWLEDGE HUB SHOWCASE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-950 text-emerald-300 text-xs font-bold border border-emerald-800">
              <BookOpen className="w-3.5 h-3.5" />
              <span>AUSTRALIAN EXPERT KNOWLEDGE BASE</span>
            </div>
            <h2 className="text-3xl font-extrabold text-white tracking-tight">
              Latest Security Guides & Recovery Playbooks
            </h2>
            <p className="text-slate-400 text-xs sm:text-sm">
              Practical guides written specifically for Australian citizens, families, and businesses.
            </p>
          </div>

          <Link
            to="/resources"
            className="inline-flex items-center gap-1 text-xs sm:text-sm font-bold text-emerald-400 hover:text-emerald-300 transition-colors"
          >
            <span>Explore All 130 Guides</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {featuredGuides.map((guide) => (
            <div
              key={guide.id}
              className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-xl flex flex-col justify-between hover:border-slate-700 transition-all group"
            >
              <div>
                {guide.imageUrl && (
                  <Link to={`/resources/${guide.slug}`} className="block relative overflow-hidden h-48">
                    <img
                      src={guide.imageUrl}
                      alt={guide.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-slate-950/80 backdrop-blur-md text-[10px] font-bold text-emerald-400 border border-slate-800 uppercase">
                      {guide.category}
                    </div>
                  </Link>
                )}

                <div className="p-6 space-y-2">
                  <div className="flex items-center justify-between text-[11px] text-slate-400 font-mono">
                    <span>{guide.date}</span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3 text-slate-500" /> {guide.readingTimeMinutes} min read
                    </span>
                  </div>

                  <h3 className="text-base font-bold text-white group-hover:text-emerald-400 transition-colors leading-snug">
                    <Link to={`/resources/${guide.slug}`}>{guide.title}</Link>
                  </h3>

                  <p className="text-xs text-slate-300 line-clamp-2 leading-relaxed">
                    {guide.summary}
                  </p>
                </div>
              </div>

              <div className="px-6 pb-6 pt-2 border-t border-slate-800 flex items-center justify-between text-xs">
                <span className="text-slate-400 font-medium text-[11px]">{guide.author.name}</span>
                <Link
                  to={`/resources/${guide.slug}`}
                  className="font-bold text-emerald-400 hover:text-emerald-300 flex items-center gap-1 group-hover:translate-x-1 transition-transform"
                >
                  <span>Read Guide</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING PREVIEW BANNER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
        <div className="max-w-2xl mx-auto space-y-3">
          <h2 className="text-3xl font-extrabold text-white">Simple, Transparent Australian Plans</h2>
          <p className="text-slate-400 text-sm">
            Start for free with on-demand breach and scam checks, or activate 24/7 continuous monitoring for you and your family.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
          {/* Free */}
          <div className="p-6 rounded-3xl bg-slate-900 border border-slate-800 space-y-4 shadow-xl">
            <h3 className="text-xl font-bold text-white">Free</h3>
            <div className="text-3xl font-extrabold text-white">$0 <span className="text-xs text-slate-400 font-normal">forever</span></div>
            <p className="text-xs text-slate-400">Essential breach search, scam checks, and basic recovery guides.</p>
            <ul className="text-xs space-y-2 text-slate-300 pt-2">
              <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Identity exposure search</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> AI Scam message analyzer</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> ID Watermark generator</li>
            </ul>
            <Link to="/register" className="block w-full py-2.5 text-center text-xs font-semibold bg-slate-800 hover:bg-slate-700 text-white rounded-xl transition-colors">
              Get Started Free
            </Link>
          </div>

          {/* Personal */}
          <div className="p-6 rounded-3xl bg-slate-900 border-2 border-emerald-500 space-y-4 relative shadow-xl shadow-emerald-950/30">
            <div className="absolute -top-3 right-4 px-2.5 py-0.5 rounded-full bg-emerald-500 text-slate-950 text-[10px] font-extrabold uppercase">
              Most Popular
            </div>
            <h3 className="text-xl font-bold text-white">Personal</h3>
            <div className="text-3xl font-extrabold text-white">$9.95 <span className="text-xs text-slate-400 font-normal">/ month</span></div>
            <p className="text-xs text-slate-400">24/7 continuous identity monitoring & fast breach alerts.</p>
            <ul className="text-xs space-y-2 text-slate-300 pt-2">
              <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Continuous dark web surveillance</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Driver licence & passport tracking</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> 8-Step Emergency Recovery Wizard</li>
            </ul>
            <Link to="/pricing" className="block w-full py-2.5 text-center text-xs font-bold bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl shadow-md transition-colors">
              Start Personal Protection
            </Link>
          </div>

          {/* Family */}
          <div className="p-6 rounded-3xl bg-slate-900 border border-slate-800 space-y-4 shadow-xl">
            <h3 className="text-xl font-bold text-white">Family</h3>
            <div className="text-3xl font-extrabold text-white">$19.95 <span className="text-xs text-slate-400 font-normal">/ month</span></div>
            <p className="text-xs text-slate-400">Total protection for up to 5 family members under one roof.</p>
            <ul className="text-xs space-y-2 text-slate-300 pt-2">
              <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Everything in Personal</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> 5 Family member profiles</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Elderly parent scam shield</li>
            </ul>
            <Link to="/pricing" className="block w-full py-2.5 text-center text-xs font-semibold bg-slate-800 hover:bg-slate-700 text-white rounded-xl transition-colors">
              Start Family Protection
            </Link>
          </div>
        </div>
      </section>

      {/* EMERGENCY CTA BAR */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="p-8 rounded-3xl bg-gradient-to-r from-rose-950/80 via-slate-900 to-slate-900 border border-rose-800/60 flex flex-col md:flex-row items-center justify-between gap-6 shadow-2xl">
          <div className="space-y-2 text-center md:text-left">
            <h3 className="text-xl font-bold text-white flex items-center justify-center md:justify-start gap-2">
              <LifeBuoy className="w-5 h-5 text-rose-400" />
              <span>Think someone has stolen your identity?</span>
            </h3>
            <p className="text-xs text-slate-300 max-w-xl">
              Don't panic. Our Emergency Response Centre provides a customized Australian recovery roadmap and connects you with IDCARE, Equifax, and state authorities.
            </p>
          </div>
          <Link
            to="/identity-theft"
            className="px-6 py-3 rounded-xl bg-rose-600 hover:bg-rose-500 text-white font-bold text-sm shadow-lg shadow-rose-950/40 transition-colors flex-shrink-0"
          >
            Start Emergency Response
          </Link>
        </div>
      </section>
    </div>
  );
};
